import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  AdminPanelSettings,
  BadgeOutlined,
  Block,
  Business,
  CheckCircle,
  Edit,
  Groups,
  People,
  Refresh,
  Save,
  Search,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import employeeService, { PERMISSION_GROUPS } from '../../services/api/employeeService';
import { friendlyError } from '../../utils/userMessages';
import {
  WorkspaceEmpty,
  WorkspaceHeader,
  WorkspacePage,
  WorkspacePanel,
  WorkspaceStatCard,
  StatusChip,
  alpha,
  dialogPaperSx,
  fieldSx,
  primaryButtonSx,
  quietButtonSx,
  tableContainerSx,
  workspacePalette as P,
} from '../common/AdminWorkspace';

const emptyEmployee = {
  firstName: '', lastName: '', displayName: '', email: '', password: '', phone: '', departmentId: '',
  designation: '', roleId: '', reportingManagerId: '', employmentType: 'full_time', joiningDate: '',
  location: '', skills: '', status: 'active',
};
const emptyRole = { name: '', description: '', permissions: [], isActive: true };
const emptyDepartment = { name: '', description: '', managerId: '', isActive: true };

const title = (value) => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, (m) => m.toUpperCase());
const employeeName = (row = {}) => String(row.displayName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email || 'Employee').trim();
const initials = (row = {}) => employeeName(row).split(/\s+/).filter(Boolean).slice(0, 2).map((v) => v[0]).join('').toUpperCase() || 'E';

export default function Employees() {
  const [tab, setTab] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [employeeForm, setEmployeeForm] = useState(emptyEmployee);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleForm, setRoleForm] = useState(emptyRole);
  const [editingRole, setEditingRole] = useState(null);
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const [departmentForm, setDepartmentForm] = useState(emptyDepartment);
  const [editingDepartment, setEditingDepartment] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [employeeRows, roleRows, departmentRows] = await Promise.all([
        employeeService.listEmployees(),
        employeeService.listEmployeeRoles(),
        employeeService.listDepartments(),
      ]);
      setEmployees(employeeRows);
      setRoles(roleRows);
      setDepartments(departmentRows);
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to load employees.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const roleMap = useMemo(() => Object.fromEntries(roles.map((row) => [row.id, row])), [roles]);
  const departmentMap = useMemo(() => Object.fromEntries(departments.map((row) => [row.id, row])), [departments]);
  const activeEmployees = useMemo(() => employees.filter((row) => row.status !== 'inactive' && row.isActive !== false), [employees]);
  const activeRoles = useMemo(() => roles.filter((row) => row.isActive !== false), [roles]);
  const activeDepartments = useMemo(() => departments.filter((row) => row.isActive !== false), [departments]);
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return employees;
    return employees.filter((row) => {
      const haystack = [
        employeeName(row), row.email, row.employeeId, row.designation, row.phone,
        departmentMap[row.departmentId]?.name, roleMap[row.roleId]?.name, row.location,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [employees, search, departmentMap, roleMap]);

  const openEmployee = (row = null) => {
    setEditingEmployee(row);
    setEmployeeForm(row ? {
      ...emptyEmployee,
      ...row,
      skills: Array.isArray(row.skills) ? row.skills.join(', ') : row.skills || '',
      password: '',
    } : { ...emptyEmployee });
    setEmployeeOpen(true);
  };

  const validateEmployee = () => {
    if (!String(employeeForm.firstName || '').trim() && !String(employeeForm.displayName || '').trim()) return 'Employee name is required.';
    if (!String(employeeForm.email || '').trim()) return 'Employee email is required.';
    if (!editingEmployee && String(employeeForm.password || '').length < 8) return 'Temporary password must contain at least 8 characters.';
    return '';
  };

  const saveEmployee = async () => {
    const validation = validateEmployee();
    if (validation) { toast.error(validation); return; }
    setSaving(true);
    try {
      if (editingEmployee) await employeeService.updateEmployee(editingEmployee.id, employeeForm);
      else await employeeService.createEmployee(employeeForm);
      toast.success(editingEmployee ? 'Employee updated.' : 'Employee created.');
      setEmployeeOpen(false);
      setEditingEmployee(null);
      setEmployeeForm({ ...emptyEmployee });
      await load();
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to save employee.'));
    } finally {
      setSaving(false);
    }
  };

  const deactivateEmployee = async (row) => {
    if (!window.confirm(`Deactivate ${employeeName(row)}? Their employee record will be retained for history.`)) return;
    try {
      await employeeService.deleteEmployee(row.id);
      toast.success('Employee access disabled.');
      await load();
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to deactivate employee.'));
    }
  };

  const openRole = (row = null) => {
    setEditingRole(row);
    setRoleForm(row ? { ...emptyRole, ...row, permissions: [...(row.permissions || [])] } : { ...emptyRole });
    setRoleOpen(true);
  };

  const saveRole = async () => {
    if (!String(roleForm.name || '').trim()) { toast.error('Role name is required.'); return; }
    setSaving(true);
    try {
      await employeeService.saveEmployeeRole({ ...roleForm, id: editingRole?.id, createdAt: editingRole?.createdAt });
      toast.success('Role saved.');
      setRoleOpen(false);
      setEditingRole(null);
      await load();
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to save role.'));
    } finally {
      setSaving(false);
    }
  };

  const removeRole = async (row) => {
    if (!window.confirm(`Disable ${row.name}? Assigned employees must be moved to another active role first.`)) return;
    try {
      await employeeService.deleteEmployeeRole(row.id);
      toast.success('Role disabled.');
      await load();
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to disable role.'));
    }
  };

  const togglePermission = (permission) => setRoleForm((previous) => ({
    ...previous,
    permissions: previous.permissions.includes(permission)
      ? previous.permissions.filter((value) => value !== permission)
      : [...previous.permissions, permission],
  }));

  const togglePermissionGroup = (permissions, enabled) => setRoleForm((previous) => {
    const set = new Set(previous.permissions || []);
    permissions.forEach((permission) => enabled ? set.add(permission) : set.delete(permission));
    return { ...previous, permissions: [...set] };
  });

  const openDepartment = (row = null) => {
    setEditingDepartment(row);
    setDepartmentForm(row ? { ...emptyDepartment, ...row } : { ...emptyDepartment });
    setDepartmentOpen(true);
  };

  const saveDepartment = async () => {
    if (!String(departmentForm.name || '').trim()) { toast.error('Department name is required.'); return; }
    setSaving(true);
    try {
      await employeeService.saveDepartment({ ...departmentForm, id: editingDepartment?.id, createdAt: editingDepartment?.createdAt });
      toast.success('Department saved.');
      setDepartmentOpen(false);
      setEditingDepartment(null);
      await load();
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to save department.'));
    } finally {
      setSaving(false);
    }
  };

  const removeDepartment = async (row) => {
    if (!window.confirm(`Disable ${row.name}? Employees must be moved out of this department first.`)) return;
    try {
      await employeeService.deleteDepartment(row.id);
      toast.success('Department disabled.');
      await load();
    } catch (error) {
      toast.error(friendlyError(error, 'Unable to disable department.'));
    }
  };

  const action = (
    <>
      <Button variant="outlined" startIcon={<Refresh />} onClick={load} sx={quietButtonSx}>Refresh</Button>
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => (tab === 0 ? openEmployee() : tab === 1 ? openRole() : openDepartment())}
        sx={primaryButtonSx}
      >
        {tab === 0 ? 'Add employee' : tab === 1 ? 'Create role' : 'Add department'}
      </Button>
    </>
  );

  return (
    <WorkspacePage>
      <WorkspaceHeader
        eyebrow="People & access"
        title="Employee Management"
        subtitle="Manage TELCOFAIR platform staff, departments and dynamic role permissions from one workspace."
        icon={<Groups />}
        action={action}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', lg: 'repeat(4,minmax(0,1fr))' }, gap: 1.1, mb: 1.35 }}>
        <WorkspaceStatCard label="Employees" value={employees.length} icon={<People />} accent={P.blue} hint="Total employee records" />
        <WorkspaceStatCard label="Dynamic roles" value={activeRoles.length} icon={<AdminPanelSettings />} accent={P.purple} hint="Permission profiles" delay={.03} />
        <WorkspaceStatCard label="Departments" value={activeDepartments.length} icon={<Business />} accent={P.teal} hint="Platform teams" delay={.06} />
        <WorkspaceStatCard label="Active staff" value={activeEmployees.length} icon={<BadgeOutlined />} accent={P.green} hint="Currently enabled" delay={.09} />
      </Box>

      <WorkspacePanel sx={{ p: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            pt: .7,
            borderBottom: `1px solid ${P.hairline}`,
            '& .MuiTab-root': { minHeight: 42, fontWeight: 820, color: P.inkLight },
            '& .Mui-selected': { color: `${P.ink} !important` },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '4px 4px 0 0', background: `linear-gradient(90deg,${P.primary},${P.secondary})` },
          }}
        >
          <Tab icon={<People sx={{ fontSize: 17 }} />} iconPosition="start" label="Employees" />
          <Tab icon={<AdminPanelSettings sx={{ fontSize: 17 }} />} iconPosition="start" label="Roles & Permissions" />
          <Tab icon={<Business sx={{ fontSize: 17 }} />} iconPosition="start" label="Departments" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: { xs: 1.15, md: 1.45 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.1 }}>
              <TextField
                placeholder="Search name, email, ID, department or role"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ ...fieldSx, width: { xs: '100%', sm: 390 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
              <Typography sx={{ color: P.inkLight, fontSize: '.66rem', fontWeight: 700 }}>{filtered.length} of {employees.length} employees</Typography>
            </Box>

            {loading ? (
              <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}><CircularProgress size={32} /></Box>
            ) : employees.length === 0 ? (
              <WorkspaceEmpty icon={<People />} title="No employees yet" text="Create your first platform employee, then assign a department and a dynamic role." actionLabel="Add employee" onAction={() => openEmployee()} />
            ) : (
              <TableContainer sx={tableContainerSx}>
                <Table size="small" sx={{ minWidth: 930 }}>
                  <TableHead><TableRow><TableCell>Employee</TableCell><TableCell>ID</TableCell><TableCell>Department</TableCell><TableCell>Role</TableCell><TableCell>Designation</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                  <TableBody>
                    {filtered.map((row, index) => {
                      const active = row.status !== 'inactive' && row.isActive !== false;
                      const avatarColors = [P.blue, P.purple, P.teal, P.green, P.coral];
                      const avatarColor = avatarColors[index % avatarColors.length];
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: .9, alignItems: 'center', minWidth: 210 }}>
                              <Avatar sx={{ width: 35, height: 35, bgcolor: alpha(avatarColor, .12), color: avatarColor, fontSize: '.68rem', fontWeight: 900, border: `1px solid ${alpha(avatarColor,.16)}` }}>{initials(row)}</Avatar>
                              <Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 820, fontSize: '.73rem', color: P.ink }} noWrap>{employeeName(row)}</Typography><Typography sx={{ fontSize: '.62rem', color: P.inkLight }} noWrap>{row.email}</Typography></Box>
                            </Box>
                          </TableCell>
                          <TableCell><Chip size="small" label={row.employeeId || '—'} sx={{ bgcolor: alpha(P.blue,.07), color: P.inkSoft }} /></TableCell>
                          <TableCell>{departmentMap[row.departmentId]?.name || '—'}</TableCell>
                          <TableCell>{roleMap[row.roleId]?.name || 'Unassigned'}</TableCell>
                          <TableCell>{row.designation || '—'}</TableCell>
                          <TableCell><StatusChip label={title(row.status || 'active')} color={active ? P.success : P.inkFaint} icon={active ? <CheckCircle /> : <Block />} /></TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEmployee(row)} sx={{ color: P.blue, bgcolor: alpha(P.blue,.06), mr: .45 }}><Edit fontSize="small" /></IconButton>
                            {active && <IconButton size="small" onClick={() => deactivateEmployee(row)} sx={{ color: P.danger, bgcolor: alpha(P.danger,.06) }}><Block fontSize="small" /></IconButton>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: { xs: 1.15, md: 1.45 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' }, gap: 1.05 }}>
            {!loading && roles.length === 0 && <Box sx={{ gridColumn: '1/-1' }}><WorkspaceEmpty icon={<AdminPanelSettings />} title="No dynamic roles" text="Create a role and select only the permissions that employee type needs." actionLabel="Create role" onAction={() => openRole()} accent={P.purple} /></Box>}
            {roles.map((row, index) => {
              const colors = [P.purple, P.blue, P.teal, P.coral];
              const accent = colors[index % colors.length];
              return (
                <WorkspacePanel key={row.id} accent={accent} hover sx={{ p: 1.35 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: .8, alignItems: 'center', minWidth: 0 }}><Box sx={{ width: 38, height: 38, borderRadius: '11px', display: 'grid', placeItems: 'center', bgcolor: alpha(accent,.09), color: accent }}><AdminPanelSettings fontSize="small" /></Box><Box sx={{ minWidth: 0 }}><Box sx={{display:'flex',alignItems:'center',gap:.45,flexWrap:'wrap'}}><Typography sx={{ fontWeight: 850, color: P.ink, fontSize: '.84rem' }}>{row.name}</Typography><StatusChip label={row.isActive === false ? 'Disabled' : 'Active'} color={row.isActive === false ? P.inkFaint : P.success} /></Box><Typography sx={{ fontSize: '.64rem', color: P.inkLight }}>{row.description || 'Dynamic employee role'}</Typography></Box></Box>
                    <Box><IconButton size="small" onClick={() => openRole(row)} sx={{ color: P.blue }}><Edit fontSize="small" /></IconButton>{row.isActive !== false && <IconButton size="small" onClick={() => removeRole(row)} sx={{ color: P.danger }} title="Disable role"><Block fontSize="small" /></IconButton>}</Box>
                  </Box>
                  <Divider sx={{ my: 1, borderColor: P.hairline }} />
                  <Box sx={{ display: 'flex', gap: .45, flexWrap: 'wrap', minHeight: 25 }}>
                    {(row.permissions || []).slice(0, 9).map((permission) => <Chip key={permission} size="small" label={permission.replaceAll('.', ' · ')} sx={{ bgcolor: alpha(accent,.065), color: P.inkSoft }} />)}
                    {(row.permissions || []).length > 9 && <Chip size="small" label={`+${row.permissions.length - 9}`} sx={{ bgcolor: alpha(accent,.09), color: accent }} />}
                    {(row.permissions || []).length === 0 && <Typography sx={{ fontSize: '.64rem', color: P.inkFaint }}>No permissions assigned</Typography>}
                  </Box>
                </WorkspacePanel>
              );
            })}
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: { xs: 1.15, md: 1.45 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))', xl: 'repeat(3,minmax(0,1fr))' }, gap: 1.05 }}>
            {!loading && departments.length === 0 && <Box sx={{ gridColumn: '1/-1' }}><WorkspaceEmpty icon={<Business />} title="No departments" text="Create departments to group platform staff and assign department managers." actionLabel="Add department" onAction={() => openDepartment()} accent={P.teal} /></Box>}
            {departments.map((row, index) => {
              const count = employees.filter((employee) => employee.departmentId === row.id).length;
              const accent = [P.teal, P.green, P.blue, P.amber][index % 4];
              return (
                <WorkspacePanel key={row.id} accent={accent} hover sx={{ p: 1.35 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}><Box sx={{ display: 'flex', gap: .65, alignItems: 'center' }}><Box sx={{ width: 36, height: 36, borderRadius: '11px', display: 'grid', placeItems: 'center', bgcolor: alpha(accent,.09), color: accent }}><Business fontSize="small" /></Box><Box><Typography sx={{ fontWeight: 850, color: P.ink, fontSize: '.84rem' }}>{row.name}</Typography><Typography sx={{ fontSize: '.61rem', color: P.inkLight }}>{count} employee{count === 1 ? '' : 's'}</Typography></Box></Box><Typography sx={{ fontSize: '.65rem', color: P.inkSoft, mt: .85, minHeight: 32 }}>{row.description || 'Platform department'}</Typography><Typography sx={{ fontSize: '.62rem', color: P.inkLight, mt: .7 }}>Manager: <b style={{ color: P.ink }}>{row.managerId && employees.find((employee) => employee.id === row.managerId) ? employeeName(employees.find((employee) => employee.id === row.managerId)) : 'Not assigned'}</b></Typography><Box sx={{mt:.65}}><StatusChip label={row.isActive === false ? 'Disabled' : 'Active'} color={row.isActive === false ? P.inkFaint : P.success} /></Box></Box>
                    <Box sx={{ flexShrink: 0 }}><IconButton size="small" onClick={() => openDepartment(row)} sx={{ color: P.blue }}><Edit fontSize="small" /></IconButton>{row.isActive !== false && <IconButton size="small" onClick={() => removeDepartment(row)} sx={{ color: P.danger }} title="Disable department"><Block fontSize="small" /></IconButton>}</Box>
                  </Box>
                </WorkspacePanel>
              );
            })}
          </Box>
        )}
      </WorkspacePanel>

      <Dialog open={employeeOpen} onClose={() => !saving && setEmployeeOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 850, color: P.ink, pb: 1 }}>{editingEmployee ? 'Edit employee' : 'Add employee'}<Typography sx={{ fontSize: '.67rem', color: P.inkLight, mt: .25 }}>{editingEmployee ? 'Update workforce profile and access assignment.' : 'Create a Firebase employee account and workforce profile.'}</Typography></DialogTitle>
        <DialogContent dividers sx={{ borderColor: P.hairline, bgcolor: '#FBFDFE' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: 1.15, pt: .2 }}>
            <TextField sx={fieldSx} label="First name" value={employeeForm.firstName} onChange={(e) => setEmployeeForm({ ...employeeForm, firstName: e.target.value })} />
            <TextField sx={fieldSx} label="Last name" value={employeeForm.lastName} onChange={(e) => setEmployeeForm({ ...employeeForm, lastName: e.target.value })} />
            <TextField sx={fieldSx} label="Display name" value={employeeForm.displayName} onChange={(e) => setEmployeeForm({ ...employeeForm, displayName: e.target.value })} />
            <TextField sx={fieldSx} label="Email" type="email" value={employeeForm.email} disabled={Boolean(editingEmployee)} helperText={editingEmployee ? 'Firebase account email is fixed from this screen.' : ''} onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })} />
            {!editingEmployee && <TextField sx={fieldSx} type="password" label="Temporary password" value={employeeForm.password} onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })} />}
            <TextField sx={fieldSx} label="Phone" value={employeeForm.phone} onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })} />
            <TextField sx={fieldSx} label="Department" select value={employeeForm.departmentId} onChange={(e) => setEmployeeForm({ ...employeeForm, departmentId: e.target.value })}><MenuItem value="">Unassigned</MenuItem>{departments.filter((row) => row.isActive !== false || row.id === editingEmployee?.departmentId).map((row) => <MenuItem key={row.id} value={row.id}>{row.name}{row.isActive === false ? ' (disabled)' : ''}</MenuItem>)}</TextField>
            <TextField sx={fieldSx} label="Dynamic role" select value={employeeForm.roleId} onChange={(e) => setEmployeeForm({ ...employeeForm, roleId: e.target.value })}><MenuItem value="">Unassigned</MenuItem>{roles.filter((row) => row.isActive !== false || row.id === editingEmployee?.roleId).map((row) => <MenuItem key={row.id} value={row.id}>{row.name}{row.isActive === false ? ' (disabled)' : ''}</MenuItem>)}</TextField>
            <TextField sx={fieldSx} label="Designation" value={employeeForm.designation} onChange={(e) => setEmployeeForm({ ...employeeForm, designation: e.target.value })} />
            <TextField sx={fieldSx} label="Reporting manager" select value={employeeForm.reportingManagerId} onChange={(e) => setEmployeeForm({ ...employeeForm, reportingManagerId: e.target.value })}><MenuItem value="">None</MenuItem>{activeEmployees.filter((row) => row.id !== editingEmployee?.id).map((row) => <MenuItem key={row.id} value={row.id}>{employeeName(row)}</MenuItem>)}</TextField>
            <TextField sx={fieldSx} label="Employment type" select value={employeeForm.employmentType} onChange={(e) => setEmployeeForm({ ...employeeForm, employmentType: e.target.value })}><MenuItem value="full_time">Full time</MenuItem><MenuItem value="part_time">Part time</MenuItem><MenuItem value="contract">Contract</MenuItem><MenuItem value="intern">Intern</MenuItem></TextField>
            <TextField sx={fieldSx} label="Joining date" type="date" InputLabelProps={{ shrink: true }} value={employeeForm.joiningDate} onChange={(e) => setEmployeeForm({ ...employeeForm, joiningDate: e.target.value })} />
            <TextField sx={fieldSx} label="Location" value={employeeForm.location} onChange={(e) => setEmployeeForm({ ...employeeForm, location: e.target.value })} />
            <TextField sx={fieldSx} label="Skills (comma separated)" value={employeeForm.skills} onChange={(e) => setEmployeeForm({ ...employeeForm, skills: e.target.value })} />
            <TextField sx={fieldSx} label="Status" select value={employeeForm.status} onChange={(e) => setEmployeeForm({ ...employeeForm, status: e.target.value })}><MenuItem value="active">Active</MenuItem><MenuItem value="on_leave">On leave</MenuItem><MenuItem value="inactive">Inactive</MenuItem></TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.4 }}><Button onClick={() => setEmployeeOpen(false)} sx={quietButtonSx}>Cancel</Button><Button variant="contained" startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <Save />} disabled={saving} onClick={saveEmployee} sx={primaryButtonSx}>Save employee</Button></DialogActions>
      </Dialog>

      <Dialog open={roleOpen} onClose={() => !saving && setRoleOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 850, color: P.ink, pb: 1 }}>{editingRole ? 'Edit dynamic role' : 'Create dynamic role'}<Typography sx={{ fontSize: '.67rem', color: P.inkLight, mt: .25 }}>Permissions are grouped by module so role scope remains clear.</Typography></DialogTitle>
        <DialogContent dividers sx={{ borderColor: P.hairline, bgcolor: '#FBFDFE' }}><Stack spacing={1.15}><TextField sx={fieldSx} label="Role name" value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} /><TextField sx={fieldSx} label="Description" value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} /><FormControlLabel control={<Switch checked={roleForm.isActive !== false} onChange={(e) => setRoleForm({ ...roleForm, isActive: e.target.checked })} />} label={<Typography sx={{fontSize:'.66rem',fontWeight:750,color:P.inkSoft}}>Role active</Typography>} />{Object.entries(PERMISSION_GROUPS).map(([group, permissions], index) => { const allSelected = permissions.every((permission) => roleForm.permissions.includes(permission)); const accent = [P.purple, P.blue, P.teal, P.green, P.amber][index % 5]; return <WorkspacePanel key={group} accent={accent} sx={{ p: 1.1 }}><Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', mb: .45 }}><Typography sx={{ fontSize: '.72rem', fontWeight: 850, color: P.ink }}>{title(group)}</Typography><FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={allSelected} onChange={(e) => togglePermissionGroup(permissions, e.target.checked)} />} label={<Typography sx={{ fontSize: '.61rem', fontWeight: 700, color: P.inkLight }}>Select all</Typography>} /></Box><Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', md: 'repeat(3,minmax(0,1fr))' }, gap: .15 }}>{permissions.map((permission) => <FormControlLabel key={permission} control={<Checkbox size="small" checked={roleForm.permissions.includes(permission)} onChange={() => togglePermission(permission)} />} label={<Typography sx={{ fontSize: '.66rem', color: P.inkSoft }}>{title(permission.split('.').slice(1).join(' '))}</Typography>} />)}</Box></WorkspacePanel>; })}</Stack></DialogContent>
        <DialogActions sx={{ p: 1.4 }}><Button onClick={() => setRoleOpen(false)} sx={quietButtonSx}>Cancel</Button><Button variant="contained" startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <Save />} disabled={saving} onClick={saveRole} sx={primaryButtonSx}>Save role</Button></DialogActions>
      </Dialog>

      <Dialog open={departmentOpen} onClose={() => !saving && setDepartmentOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 850, color: P.ink }}>{editingDepartment ? 'Edit department' : 'Add department'}</DialogTitle>
        <DialogContent dividers sx={{ borderColor: P.hairline, bgcolor: '#FBFDFE' }}><Stack spacing={1.15}><TextField sx={fieldSx} label="Department name" value={departmentForm.name} onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })} /><TextField sx={fieldSx} label="Description" multiline minRows={3} value={departmentForm.description} onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })} /><TextField sx={fieldSx} label="Manager" select value={departmentForm.managerId} onChange={(e) => setDepartmentForm({ ...departmentForm, managerId: e.target.value })}><MenuItem value="">Not assigned</MenuItem>{activeEmployees.map((row) => <MenuItem key={row.id} value={row.id}>{employeeName(row)}</MenuItem>)}</TextField><FormControlLabel control={<Switch checked={departmentForm.isActive !== false} onChange={(e) => setDepartmentForm({ ...departmentForm, isActive: e.target.checked })} />} label={<Typography sx={{fontSize:'.66rem',fontWeight:750,color:P.inkSoft}}>Department active</Typography>} /></Stack></DialogContent>
        <DialogActions sx={{ p: 1.4 }}><Button onClick={() => setDepartmentOpen(false)} sx={quietButtonSx}>Cancel</Button><Button variant="contained" startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <Save />} disabled={saving} onClick={saveDepartment} sx={primaryButtonSx}>Save department</Button></DialogActions>
      </Dialog>
    </WorkspacePage>
  );
}
